import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Field, PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Car, Plus, Pencil, Trash2, Search } from "lucide-react";
import { etiquetaMatricula } from "@/components/MatriculaSelect";
import { esMatricula } from "@/lib/validacionEs";
import { LEN } from "@/lib/limites";

const i = String.fromCharCode(0xed);
const o = String.fromCharCode(0xf3);
const a = String.fromCharCode(0xe1);
const e = String.fromCharCode(0xe9);
const n = String.fromCharCode(0xf1);

const TIPO_VEH = "vehiculo";
const TIPO_REM = "remolque";
const PAGE_SIZE = 50;

function claveTra(t) {
  const nif = String(t?.nif || t?.transportista_nif || "").trim().toUpperCase();
  if (nif) return nif;
  if (t?.codigo != null && t.codigo !== "") return "COD-" + String(t.codigo);
  return "";
}

function etiquetaTra(t) {
  return (t?.razon_social || t?.transportista_razon_social || "").trim()
    || (t?.codigo != null && t.codigo !== "" ? "C" + o + "d. " + t.codigo : claveTra(t));
}

function nifReal(t) {
  const n = String(t?.nif || "").trim();
  if (!n || n.toUpperCase().startsWith("COD-")) return "";
  return n;
}

function mismoTra(a, b) {
  if (!a || !b) return false;
  const na = nifReal(a);
  const nb = nifReal(b);
  if (na && nb) return na === nb;
  const ca = String(a.codigo ?? "");
  const cb = String(b.codigo ?? "");
  return ca !== "" && ca === cb;
}

function esSageMat(c) {
  return c?.sage === true || c?.fuente === "sage" || String(c?.id || "").startsWith("sage-");
}

function labelTipo(tipo) {
  return tipo === TIPO_REM ? "Remolque" : "Cami" + o + "n / furgoneta";
}

function TablaFlota({ titulo, items, onNew, onEdit, onRemove }) {
  return (
    <div className="mb-6 last:mb-0">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="label-tag mb-0">{titulo}</div>
        <Button size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={onNew}><Plus className="h-4 w-4 mr-2"/> Nueva</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="deca-table w-full">
          <thead>
            <tr>
              <th>{"Matr" + i + "cula"}</th>
              <th>Alias</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td className="font-mono font-medium">{c.matricula}</td>
                <td className="text-sm">{c.alias || "\u2014"}</td>
                <td className="text-right whitespace-nowrap">
                  {esSageMat(c) ? <span className="text-[11px] text-slate-400">Sage</span> : (
                    <>
                  <Button size="sm" variant="outline" className="mr-2" onClick={()=>onEdit(c)} aria-label={"Editar matr" + i + "cula"}><Pencil className="h-3.5 w-3.5" aria-hidden="true"/></Button>
                  <Button size="sm" variant="outline" className="text-red-700" onClick={()=>onRemove(c)} aria-label={"Eliminar matr" + i + "cula"}><Trash2 className="h-3.5 w-3.5" aria-hidden="true"/></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 ? (
              <tr><td colSpan={3} className="py-8 text-center text-slate-500">{"Todav" + i + "a no hay " + titulo.toLowerCase() + "."}</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function MatriculasMantPage() {
  const { user } = useAuth();
  const usaSage = user?.sage_conectado === true;
  const [transportistas, setTransportistas] = useState([]);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ tipo: TIPO_VEH, matricula: "", alias: "" });
  const [loadingTra, setLoadingTra] = useState(false);

  const loadTransportistas = async () => {
    setLoadingTra(true);
    try {
      const { data: partes } = await api.get("/api/v1/partes", {
        params: { tipo: "transportista", page: 1, page_size: PAGE_SIZE, buscar: q || undefined },
      });
      const list = [];
      const seen = new Set();
      const add = (t) => {
        const key = claveTra(t) || etiquetaTra(t).toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        list.push({
          nif: nifReal(t) || claveTra(t),
          codigo: t.codigo,
          razon_social: etiquetaTra(t),
        });
      };
      (partes.items || []).forEach(add);
      if (!usaSage) {
        const [{ data }, { data: cats }] = await Promise.all([
          api.get("/api/v1/decas/manual/opciones"),
          api.get("/api/v1/matriculas"),
        ]);
        (data.transportistas || []).forEach(add);
        for (const c of cats.items || []) {
          add({ nif: c.transportista_nif, razon_social: c.transportista_razon_social, codigo: c.transportista_codigo });
        }
      }
      list.sort((a, b) => (a.razon_social || "").localeCompare(b.razon_social || "", "es"));
      setTransportistas(list);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingTra(false);
    }
  };

  const loadItems = async (t) => {
    if (!t) { setItems([]); return; }
    const nif = nifReal(t);
    const codigo = t.codigo != null && t.codigo !== "" ? t.codigo : undefined;
    if (!nif && !codigo) { setItems([]); return; }
    try {
      const { data } = await api.get("/api/v1/matriculas", {
        params: { transportista_nif: nif || undefined, transportista_codigo: codigo },
      });
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);
  useEffect(() => { loadTransportistas(); }, [q, usaSage]); // eslint-disable-line
  useEffect(() => { loadItems(sel); }, [sel]); // eslint-disable-line

  const filtrados = transportistas;

  const vehiculos = items.filter((x) => (x.tipo || TIPO_VEH) !== TIPO_REM);
  const remolques = items.filter((x) => x.tipo === TIPO_REM);

  const openNew = (tipo) => {
    setEdit(null);
    setForm({ tipo, matricula: "", alias: "" });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEdit(c);
    setForm({ tipo: c.tipo || TIPO_VEH, matricula: c.matricula || "", alias: c.alias || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!sel || (!nifReal(sel) && (sel.codigo == null || sel.codigo === ""))) return;
    if (!form.matricula.trim()) { toast.error("Indica la matr" + i + "cula"); return; }
    if (!esMatricula(form.matricula)) {
      toast.error("Matr" + i + "cula no v" + a + "lida (espa" + n + "ola o extranjera: letras y n" + String.fromCharCode(0xfa) + "meros)");
      return;
    }
    try {
      const payload = {
        transportista_nif: nifReal(sel) || claveTra(sel),
        transportista_razon_social: sel.razon_social,
        tipo: form.tipo,
        matricula: form.matricula.trim(),
        alias: form.alias.trim() || null,
        activo: true,
      };
      if (edit) await api.patch(`/api/v1/matriculas/${edit.id}`, payload);
      else await api.post("/api/v1/matriculas", payload);
      setOpen(false);
      await loadItems(sel);
      toast.success("Matr" + i + "cula guardada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/api/v1/matriculas/${c.id}`);
      await loadItems(sel);
      toast.success("Matr" + i + "cula eliminada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div>
      <PageHeader
        title={"Matr" + i + "culas por transportista"}
        subtitle={"Cami" + o + "n, furgoneta y remolque de cada agencia. Si hay mantenimiento de conductores, Matricula es el cami" + o + "n y Matricula2 el remolque."}
        testid="page-matriculas-mant"
      />
      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        <Card className="p-3">
          <div className="relative mb-2">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400"/>
            <Input className="pl-8" value={qInput} onChange={(ev)=>setQInput(ev.target.value)} placeholder={"Buscar transportista, NIF o c" + o + "digo"} aria-label="Buscar transportista o NIF"/>
          </div>
          <div className="max-h-[70vh] overflow-auto space-y-1">
            {filtrados.map((t) => (
              <button
                type="button"
                key={t.nif || t.codigo || t.razon_social}
                onClick={() => setSel(t)}
                className={`w-full text-left rounded-md px-3 py-2 border ${mismoTra(sel, t) ? "border-blue-700 bg-blue-50" : "border-transparent hover:bg-slate-50"}`}
              >
                <div className="text-sm font-semibold text-slate-900 truncate">{t.razon_social}</div>
                <div className="text-[11px] font-mono text-slate-500">
                  {nifReal(t) ? nifReal(t) : (t.codigo != null && t.codigo !== "" ? "C" + o + "d. " + t.codigo : "sin NIF")}
                </div>
              </button>
            ))}
            {loadingTra ? <div className="text-sm text-slate-500 p-3">Cargando{String.fromCharCode(0x2026)}</div> : null}
            {!loadingTra && filtrados.length === 0 ? <div className="text-sm text-slate-500 p-3">{q ? "Sin coincidencias en Sage." : ("No hay transportistas. Cr" + e + "alos en Transportistas o vendr" + a + "n de Sage.")}</div> : null}
          </div>
        </Card>

        <Card className="p-4">
          {!sel ? (
            <div className="py-16 text-center text-slate-500">
              <Car className="h-8 w-8 mx-auto text-slate-400 mb-2"/>
              Selecciona un transportista a la izquierda.
            </div>
          ) : (
            <>
              <div className="mb-4">
                <div className="label-tag">Transportista</div>
                <div className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Chivo" }}>{sel.razon_social}</div>
                <div className="text-xs font-mono text-slate-500">
                  {sel.nif && !String(sel.nif).startsWith("COD-") ? ("NIF " + sel.nif) : (sel.codigo != null && sel.codigo !== "" ? "C" + o + "d. " + sel.codigo : "")}
                </div>
              </div>
              <TablaFlota
                titulo={"Cami" + o + "nes / furgonetas"}
                items={vehiculos}
                onNew={() => openNew(TIPO_VEH)}
                onEdit={openEdit}
                onRemove={remove}
              />
              <TablaFlota
                titulo="Remolques"
                items={remolques}
                onNew={() => openNew(TIPO_REM)}
                onEdit={openEdit}
                onRemove={remove}
              />
            </>
          )}
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{edit ? "Editar matr" + i + "cula" : "Nueva matr" + i + "cula"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">{sel?.razon_social} {sel?.nif ? `(${sel.nif})` : ""} \u00b7 {labelTipo(form.tipo)}</div>
            <Field label={"Matr" + i + "cula *"}>
              <Input className="mt-1 font-mono uppercase" maxLength={LEN.matricula} value={form.matricula} onChange={(ev)=>setForm({...form, matricula:ev.target.value})}/>
            </Field>
            <Field label="Alias (opcional)">
              <Input className="mt-1" maxLength={LEN.alias} value={form.alias} onChange={(ev)=>setForm({...form, alias:ev.target.value})} placeholder="Furgoneta 1, semirremolque..."/>
            </Field>
            {form.matricula ? <div className="text-xs text-slate-500">{"En el DeCA saldr" + a + ": "}<b>{etiquetaMatricula(form)}</b></div> : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
